<template>
  <div class="admin-page">
    <header class="admin-page__header">
      <div>
        <h1 class="admin-page__title">实体类别管理</h1>
        <p class="admin-page__subtitle">
          按图谱维护实体类型编码与名称
          <template v-if="graphId != null && !state.loading">
            ·
            <span class="admin-category-summary--inline">
              <template v-if="hasSearchKeyword">
                匹配 <strong>{{ matchCount }}</strong> / 共
                <strong>{{ totalCount }}</strong> 个类别
              </template>
              <template v-else>
                共 <strong>{{ totalCount }}</strong> 个类别
              </template>
            </span>
          </template>
        </p>
      </div>
      <div class="admin-page__actions">
        <el-input
          v-model="state.keywords"
          placeholder="搜索名称/编码"
          style="width: 200px"
          clearable
          :disabled="graphId == null"
        />
      </div>
    </header>

    <section class="admin-panel admin-panel--tag-wall">
      <CategoryTagWall
        :categories="state.categoryList"
        :loading="state.loading"
        :disabled="graphId == null"
        :search-keyword="state.keywords"
        @create="openCreateDialog"
        @edit="openEditDialog"
        @delete="confirmDelete"
      />
    </section>

    <el-dialog
      v-model="state.dialogVisible"
      :title="dialogTitle"
      width="440px"
      destroy-on-close
      @closed="resetDialog"
    >
      <el-form
        ref="formRef"
        :model="state.form"
        :rules="formRules"
        label-width="90px"
      >
        <el-form-item label="编码" prop="code">
          <el-input
            v-model="state.form.code"
            :placeholder="
              state.dialogMode === 'create'
                ? '手动输入，图内唯一'
                : '未被引用时可修改'
            "
          />
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="state.form.name" placeholder="类别显示名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="state.dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="state.submitting" @click="submit">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import CategoryTagWall from "@/components/admin/CategoryTagWall.vue";
import { useAdminSelectedGraph } from "@/composables/useAdminSelectedGraph";
import { showApiErrorMessage } from "@/api/httpService";
import {
  createEntityCategory,
  deleteEntityCategory,
  listEntityCategories,
  updateEntityCategoryCode,
  updateEntityCategoryName,
} from "@/api/adminEntityCategoryApi";

const formRef = ref(null);
const { graphId, ensureGraphOptionsLoaded } = useAdminSelectedGraph();

const state = reactive({
  loading: false,
  submitting: false,
  keywords: "",
  categoryList: [],
  dialogVisible: false,
  dialogMode: "create",
  editingId: null,
  originalCode: "",
  originalName: "",
  form: {
    code: "",
    name: "",
  },
});

const formRules = {
  code: [{ required: true, message: "请输入编码", trigger: "blur" }],
  name: [{ required: true, message: "请输入名称", trigger: "blur" }],
};

const dialogTitle = computed(() =>
  state.dialogMode === "create" ? "新建实体类别" : "编辑实体类别"
);

const hasSearchKeyword = computed(
  () => (state.keywords || "").trim().length > 0
);

const totalCount = computed(() => state.categoryList.length);

const matchCount = computed(() => {
  const kw = (state.keywords || "").trim().toLowerCase();
  if (!kw) {
    return totalCount.value;
  }
  return state.categoryList.filter((item) => {
    const name = (item.name || "").toLowerCase();
    const code = (item.code || "").toLowerCase();
    return name.includes(kw) || code.includes(kw);
  }).length;
});

function showApiError(res) {
  showApiErrorMessage(res);
}

async function loadCategories() {
  if (graphId.value == null) {
    state.categoryList = [];
    return;
  }
  state.loading = true;
  try {
    const res = await listEntityCategories(graphId.value);
    if (res?.code === 0) {
      state.categoryList = res.data || [];
    } else {
      showApiError(res);
    }
  } finally {
    state.loading = false;
  }
}

function onGraphChange() {
  state.keywords = "";
  loadCategories();
}

watch(graphId, (id, prev) => {
  if (id === prev) {
    return;
  }
  onGraphChange();
});

function openCreateDialog() {
  state.dialogMode = "create";
  state.editingId = null;
  state.form.code = "";
  state.form.name = "";
  state.dialogVisible = true;
}

function openEditDialog(row) {
  state.dialogMode = "edit";
  state.editingId = row.id;
  state.originalCode = row.code;
  state.originalName = row.name;
  state.form.code = row.code;
  state.form.name = row.name;
  state.dialogVisible = true;
}

function resetDialog() {
  state.form.code = "";
  state.form.name = "";
  state.editingId = null;
  state.originalCode = "";
  state.originalName = "";
}

async function submit() {
  if (!formRef.value) {
    return;
  }
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) {
    return;
  }
  state.submitting = true;
  try {
    if (state.dialogMode === "create") {
      const res = await createEntityCategory(
        graphId.value,
        state.form.code.trim(),
        state.form.name.trim()
      );
      if (res?.code === 0) {
        ElMessage.success("操作成功");
        state.dialogVisible = false;
        await loadCategories();
      } else {
        showApiError(res);
      }
      return;
    }

    const name = state.form.name.trim();
    const code = state.form.code.trim();
    const nameChanged = name !== state.originalName;
    const codeChanged = code !== state.originalCode;

    if (!nameChanged && !codeChanged) {
      state.dialogVisible = false;
      return;
    }

    if (nameChanged) {
      const nameRes = await updateEntityCategoryName(state.editingId, name);
      if (nameRes?.code !== 0) {
        showApiError(nameRes);
        return;
      }
    }
    if (codeChanged) {
      const codeRes = await updateEntityCategoryCode(state.editingId, code);
      if (codeRes?.code !== 0) {
        showApiError(codeRes);
        return;
      }
    }

    ElMessage.success("操作成功");
    state.dialogVisible = false;
    await loadCategories();
  } finally {
    state.submitting = false;
  }
}

async function confirmDelete(row) {
  const usage = Number(row.entityCount) || 0;
  const usageHint =
    usage > 0
      ? `当前有 ${usage} 个实体引用该类别，删除将失败。`
      : "若已被实体引用将无法删除。";
  try {
    await ElMessageBox.confirm(
      `确定删除类别「${row.name}」（${row.code}）吗？${usageHint}`,
      "删除类别",
      { type: "warning" }
    );
    const res = await deleteEntityCategory(row.id);
    if (res?.code === 0) {
      ElMessage.success("已删除");
      await loadCategories();
    } else {
      showApiError(res);
    }
  } catch {
    // 用户取消
  }
}

onMounted(async () => {
  await ensureGraphOptionsLoaded();
  if (graphId.value != null) {
    await loadCategories();
  }
});
</script>

<style scoped lang="scss">
.admin-category-summary--inline {
  color: inherit;

  strong {
    font-weight: 600;
    color: #1d2129;
  }
}
</style>
